import {
  CalendarAltIcon,
  InfoIcon,
  LocationAltIcon,
  PhoneIcon,
  RmxEditIcon
} from 'cokitchen-icons';
import { ReactComponent as CutleryIcon } from 'assets/icons/cutlery.svg';
import { ReactComponent as QuestionIcon } from 'assets/icons/question-icon-filled.svg';
import useDashboardContext from 'contexts/dashboard.context';
import React, { Fragment, useEffect, useState } from 'react';
import { Button, Input, ToastMessage } from '@cokitchen/cokitchen-components';
import '../../styles.scss';
import {
  checkForMultipleBrands,
  formatCurrency,
  formatDatetime,
  formatDaysOfWeek,
  getTimeRange,
  handleApiError,
  truncateString
} from 'utils/functions';
import MealItem from 'components/meal-item';
import ScheduledDeliveryModal from 'components/modals/scheduled-delivery-modal';
import DiscountCodeModal from 'components/modals/discount-code-modal';
import AppreciationModal from 'components/modals/appreciation-note-modal';
import AddNewNumberModal from 'components/modals/add-new-number';
import useOrdersContext from 'contexts/orders.context';
import AddressModal from 'components/modals/group-order/address-modal';
import { useNavigate } from 'react-router-dom';
import { ROUTE_KEYS, STORAGE_KEYS } from 'utils/constants';
import moment from 'moment';
import { ENUM_BRAND_TYPE, ENUM_ORDERS_TYPE } from 'utils/enum';
import CancelCalculatedOrderModal from 'components/modals/cancel-calculated-order-modal';
import BulkOrderService from 'services/bulk-order.service';
import { OrderMealType } from 'models/order.model';
import { LocationObjectType } from 'models/location.model';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';

export type FormattedOrderType = {
  brand: { name: string; id: string };
  meals: OrderMealType[];
};

const BatchBasket = () => {
  const { setIsBackLink, setBackLinkTitle, setBackLink, activeCompany } = useDashboardContext();
  const { batchOrderMeals, setBatchOrderMeals, calculatedOrderLoading, deliveryDate } =
    useOrdersContext();
  const [showScheduledDeliveryModal, setShowScheduledDeliveryModal] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showPhoneNumberModal, setShowPhoneNumberModal] = useState(false);
  const [showAppreciationNoteModal, setShowAppreciationNoteModal] = useState(false);
  const [showDiscountCodeModal, setShowDiscountCodeModal] = useState(false);
  const [showCancelOrderModal, setShowCancelOrderModal] = useState(false);
  const [formattedOrders, setFormattedOrders] = useState<FormattedOrderType[]>([]);
  const [isDisabled, setIsDisabled] = useState(false);
  const DEFAULT_PHONE_NUMBER = localStorage.getItem(STORAGE_KEYS.PHONE_NUMBER);

  const {
    calculatedOrderForm,
    setcalculatedOrderForm,
    calculatedOrder,
    createOrderForm,
    setCreateOrderForm
  } = useOrdersContext();
  const navigate = useNavigate();
  const [brands] = useState(checkForMultipleBrands(calculatedOrder));

  const getBrandDeliveryTime = async (brand_id: string) => {
    if (!activeCompany.id || !brand_id) return;
    try {
      const res = await apiService.get<{
        data: {
          delivery_time: string;
        };
      }>(`company/${activeCompany.id}/delivery-time`, {
        params: {
          brand_id
        }
      });
      setcalculatedOrderForm({
        ...calculatedOrderForm,
        scheduled_delivery_time: getTimeRange(res.data.data.delivery_time),
        scheduled_delivery_date: deliveryDate
      });
    } catch (error) {
      handleApiError(error);
    }
  };

  useEffect(() => {
    if (brands) {
      if (brands.size > 1) {
        toast(
          <ToastMessage type='error' message={'You can only order from one FCB brand at a time'} />
        );
      } else {
        const brand = brands.values().next().value;
        if (brand?.id && brand?.brand_type !== ENUM_BRAND_TYPE.FOODCOURT_BUSINESS)
          getBrandDeliveryTime(brand.id);
      }
    }
  }, [brands]);

  useEffect(() => {
    setIsBackLink(true);
    setBackLinkTitle('Return to previous');
    setBackLink(ROUTE_KEYS.BATCHED_ORDERS_BRANDS);

    return () => {
      setIsBackLink(false);
      setBackLinkTitle('');
      setBackLink('');
    };
  }, []);

  const confirm = () => {
    navigate(`/orders/checkout?type=${ENUM_ORDERS_TYPE.BATCH}`);
  };
  useEffect(() => {
    BulkOrderService.FormatOrder(batchOrderMeals, setFormattedOrders);
  }, [batchOrderMeals]);

  useEffect(() => {
    isConfirmDisabled();
  }, [calculatedOrderForm, batchOrderMeals]);

  const isConfirmDisabled = () => {
    setIsDisabled(false);
    batchOrderMeals.map(({ quantity, members }) => {
      if (quantity < (members?.length || 0)) {
        setIsDisabled(true);
      }
    });
    if (
      !calculatedOrderForm.recurring &&
      !(
        (calculatedOrderForm.scheduled_delivery_date &&
          calculatedOrderForm.scheduled_delivery_time) ||
        (calculatedOrderForm.preorder_delivery_date && calculatedOrderForm.preorder_delivery_time)
      )
    ) {
      setIsDisabled(true);
    }
  };

  const editOrder = (meal: OrderMealType) => {
    navigate(`/orders/meals/${meal.id}?type=batch`, { state: { created_at: meal.created_at } });
  };

  const getDeliveryDatetime = () => {
    if (
      calculatedOrderForm?.preorder &&
      calculatedOrderForm?.preorder_delivery_date &&
      calculatedOrderForm?.preorder_delivery_time
    )
      return formatDatetime(
        calculatedOrderForm.preorder_delivery_date,
        calculatedOrderForm.preorder_delivery_time
      );
    if (
      calculatedOrderForm?.scheduled_delivery_date &&
      calculatedOrderForm?.scheduled_delivery_time
    )
      return formatDatetime(
        calculatedOrderForm.scheduled_delivery_date,
        calculatedOrderForm.scheduled_delivery_time
      );
    return 'Schedule a delivery';
  };

  const subTotal =
    Number(calculatedOrder?.total_amount) -
    Number(calculatedOrder?.delivery_fee) -
    Number(calculatedOrder?.delivery_service_charge) -
    Number(calculatedOrder?.service_charge);
  const deliveryFee =
    Number(calculatedOrder?.delivery_fee) + Number(calculatedOrder?.delivery_service_charge);
  const discountApplicable =
    activeCompany.delivery_discounts.batch_order && !calculatedOrderForm.preorder;

  const handleUpdateCalculatedOrderForm = (selectedAddress: LocationObjectType) => {
    if (selectedAddress) {
      setcalculatedOrderForm({
        ...calculatedOrderForm,
        address_details: {
          name: selectedAddress.name,
          address_line: selectedAddress.address,
          city: selectedAddress.address.split(',')[1] ?? '',
          phone_number: DEFAULT_PHONE_NUMBER ?? '',
          building_number: selectedAddress.street_number ?? ''
        },
        lat: selectedAddress.lat.toString(),
        lng: selectedAddress.lng.toString()
      });
    }
  };

  return (
    <Fragment>
      {
        <div
          className={`w-full ${
            calculatedOrderLoading ? 'opacity-30 cursor-not-allowed pointer-events-none ' : ''
          }`}>
          <div className='bg-[#F7F7F8] w-full h-[85vh] rounded-xl p-1 flex flex-col-reverse lg:flex-row gap-2 lg:gap-6 lg:justify-between basket'>
            <div className='w-full lg:w-[48.5%] h-full relative lg:pb-14'>
              <div className='w-full h-[30vh] pb-12 mt-[-2rem] lg:mt-0 lg:pb-0 lg:h-[80%] overflow-y-scroll no-scrollbar'>
                <div className='w-full bg-white pt-7 px-6 pb-4 rounded-tl-lg mb-3'>
                  <h4 className='mb-3 font-semibold text-[#2E3031] text-2xl'>Delivery details</h4>
                  <div className='w-full cursor-pointer flex justify-between items-center pb-3 border-b-[0.7px] border-b-[#EAEBEB]'>
                    <div className='flex gap-2 text-gray-9 items-center'>
                      <CalendarAltIcon />
                      <div>
                        <p className='text-gray-10'>{getDeliveryDatetime()}</p>
                        {calculatedOrderForm.recurring && (
                          <p className='text-[#6F7376] text-xs'>
                            {calculatedOrderForm.recurrence === 'weekly' ? (
                              <>
                                {' '}
                                Repeats weekly on{' '}
                                <span className='text-[#0080E5]'>
                                  {formatDaysOfWeek(calculatedOrderForm.selected_days)}
                                </span>
                              </>
                            ) : (
                              'Repeats daily'
                            )}{' '}
                            until {''}
                            {moment(calculatedOrderForm.recurring_order_end_date).format(
                              'Do MMM YYYY'
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className='w-full flex justify-between pb-3 border-b-[0.7px] border-b-[#EAEBEB] items-center mt-4 text-gray-6'>
                    <div className='w-[85%] flex gap-2 text-gray-9 items-center'>
                      <LocationAltIcon />
                      <div className='w-[95%]'>
                        <p className='text-gray-6 line-clamp-1'>
                          {calculatedOrderForm?.address_details?.address_line}
                        </p>
                      </div>
                    </div>
                    <RmxEditIcon
                      onClick={() => setShowAddressModal(true)}
                      className='cursor-pointer'
                    />
                  </div>
                  <div className='w-full flex justify-between items-center mt-4 text-gray-6'>
                    <div className='flex gap-2 text-gray-9 items-center'>
                      <PhoneIcon />
                      <p className='text-gray-6'>
                        {calculatedOrderForm?.address_details?.phone_number
                          ? calculatedOrderForm?.address_details?.phone_number
                          : 'Add Phone Number'}
                      </p>
                    </div>
                    <RmxEditIcon
                      className='cursor-pointer'
                      onClick={() => setShowPhoneNumberModal(true)}
                    />
                  </div>
                </div>
                <div className='w-full py-5 px-6 flex justify-between items-center bg-white gap-4 mb-3'>
                  <div className='flex gap-2'>
                    <CutleryIcon />
                    {createOrderForm?.order_details?.cutlery ? (
                      <div>
                        <p className='text-[#2E3031] text-sm'>Cutlery included</p>
                        <p className='text-[#6F7376] text-xs'>
                          Help us <span className='text-[#3FB827]'>reduce plastic waste</span> by
                          removing cutlery if not needed
                        </p>
                      </div>
                    ) : (
                      <div>
                        <p className='text-[#2E3031] text-sm'>No cutlery included</p>
                        <p className='text-[#6F7376] text-sm'>
                          Tap to add cutlery if you need it (free)
                        </p>
                      </div>
                    )}
                  </div>
                  <Input
                    type='checkbox'
                    inputClass='w-4 h-4'
                    checked={createOrderForm?.order_details?.cutlery}
                    readOnly
                    onChange={(e) =>
                      setCreateOrderForm({
                        ...createOrderForm,
                        order_details: {
                          ...createOrderForm.order_details,
                          cutlery: e.target.checked
                        }
                      })
                    }
                  />
                </div>
                <div className='w-full py-5 px-6 bg-white mb-3 '>
                  <div className='w-full mt-4 flex justify-between'>
                    <p className='text-[#6F7376] text-sm'>
                      {createOrderForm.order_details.appreciation_note &&
                      createOrderForm.order_details.appreciation_note !== ''
                        ? truncateString(createOrderForm.order_details.appreciation_note, 4)
                        : 'Appreciation note'}
                    </p>
                    <p
                      className='text-red-5 text-sm font-medium cursor-pointer'
                      onClick={() => setShowAppreciationNoteModal(true)}>
                      {createOrderForm.order_details.appreciation_note === '' ? 'Add' : 'Edit'}
                    </p>
                  </div>
                  <div className='w-full mt-4 flex justify-between'>
                    <p className='text-[#6F7376] text-sm'>
                      {calculatedOrderForm.discount_code && calculatedOrderForm.discount_code !== ''
                        ? calculatedOrderForm.discount_code
                        : 'Discount code'}
                    </p>
                    <p
                      className='text-red-5 text-sm font-medium cursor-pointer'
                      onClick={() => setShowDiscountCodeModal(true)}>
                      {calculatedOrderForm.discount_code === '' ? 'Add' : 'Edit'}
                    </p>
                  </div>
                </div>
                {calculatedOrder?.address_details?.address_line === '' && (
                  <div className='bg-[#FFF4E5] py-2 px-6 text-sm text-[#FF9500]'>
                    <p className='flex gap-2 items-center'>
                      {' '}
                      <InfoIcon /> Kindly, select address to proceed with your order
                    </p>
                  </div>
                )}
                <div className='w-full py-4 px-6 bg-white mb-3'>
                  <div className='flex justify-between mb-3'>
                    <p className='text-[#6F7376] text-sm'>Subtotal</p>
                    <p className='text-[#6F7376] text-sm font-medium'>{formatCurrency(subTotal)}</p>
                  </div>
                  <div className='flex justify-between mb-3'>
                    <p className='text-[#6F7376] text-sm'>
                      Delivery fee&nbsp;
                      {discountApplicable &&
                      !(activeCompany.delivery_discounts.batch_order === 100) ? (
                        <span className='text-green-5'>
                          ({activeCompany.delivery_discounts.batch_order + '% off'})
                        </span>
                      ) : null}
                    </p>
                    <p className='text-[#6F7376] text-sm font-medium'>
                      {activeCompany.delivery_discounts.batch_order === 100 ? (
                        <span className='text-green-5'>FREE</span>
                      ) : (
                        formatCurrency(deliveryFee)
                      )}
                    </p>
                  </div>
                  <div className='flex justify-between mb-3'>
                    <div className='flex gap-1 items-center'>
                      <p className='text-[#6F7376] text-sm'>Service fee</p>
                      <QuestionIcon className='question-icon' />
                    </div>
                    <p className='text-[#6F7376] text-sm font-medium'>
                      {formatCurrency(calculatedOrder?.service_charge)}
                    </p>
                  </div>
                  {calculatedOrderForm?.discount_code !== '' &&
                    calculatedOrder?.prev_price &&
                    calculatedOrder?.prev_price !== '' && (
                      <div className='flex justify-between mb-3'>
                        <div className='flex gap-1 items-center'>
                          <p className='text-[#6F7376] text-sm'>Discount code</p>
                        </div>
                        <p className='text-[#6F7376] text-sm font-medium'>
                          -
                          {formatCurrency(
                            Number(calculatedOrder?.prev_price) - calculatedOrder.total_amount
                          )}
                        </p>
                      </div>
                    )}
                </div>
              </div>
              <div className='w-full rounded-bl-lg bg-white p-5 px-6 absolute bottom-0 left-0 total-box'>
                {discountApplicable && !(activeCompany.delivery_discounts.batch_order === 100) ? (
                  <article className='flex justify-between mb-3 text-sm text-green-5'>
                    <p>You are saving</p>
                    <p className='font-medium'>
                      {formatCurrency(
                        (activeCompany.delivery_discounts.batch_order / 100) *
                          (deliveryFee / (1 - activeCompany.delivery_discounts.batch_order / 100))
                      )}
                    </p>
                  </article>
                ) : null}
                <div className='flex justify-between mb-3'>
                  <p className='text-[#6F7376] text-sm'>Order total</p>
                  <p className='text-[#2E3031] text-sm font-medium'>
                    {formatCurrency(calculatedOrder?.total_amount)}
                  </p>
                </div>
                <Button
                  variant='block'
                  color='primary'
                  className='w-full h-[53px] !max-w-full'
                  onClick={confirm}
                  disabled={isDisabled}>
                  Confirm Order
                </Button>
              </div>
            </div>
            <div className='w-full lg:w-[48.5%] h-full overflow-y-scroll  no-scrollbar'>
              <div className='w-full pt-7 px-6 pb-2 bg-white rounded-tr-lg'>
                <div className='w-full flex justify-between'>
                  <h4 className='mb-3 font-semibold text-[#2E3031] text-xl'>Order summary</h4>
                  <div className='cursor-pointer' onClick={() => setShowCancelOrderModal(true)}>
                    <p className='text-red-5 text-sm'>Clear basket</p>
                  </div>
                </div>
              </div>
              <div className='w-full bg-white min-h-full py-[10px]'>
                {formattedOrders.length === 0 ? (
                  <div className='flex items-center justify-center gap-2 py-12 '>
                    <div className='min-w-full px-6'>
                      <h4 className='font-medium mb-9 text-[#6F7376] text-sm text-center'>
                        Select meals to order from different restaurants
                      </h4>
                      <Button
                        variant='block'
                        color='primary'
                        small
                        className='mx-auto'
                        onClick={() => navigate(-1)}>
                        Go back to order
                      </Button>
                    </div>
                  </div>
                ) : (
                  formattedOrders?.map((restaurantMeal, index) => (
                    <div
                      className='flex flex-col border-b-[6px] border-[#F7F7F7] last:border-b-0'
                      key={index}>
                      <div className='flex items-center gap-2 py-4 px-7'>
                        <h4 className='font-medium'>{restaurantMeal.brand.name}</h4>
                      </div>
                      <ul>
                        {restaurantMeal?.meals?.map((meal, index) => (
                          <MealItem
                            onClick={() => editOrder(meal)}
                            type='batch'
                            key={index}
                            meal={meal}
                            removeMeal={() =>
                              BulkOrderService.RemoveMealFromOrder(
                                meal,
                                batchOrderMeals,
                                setBatchOrderMeals
                              )
                            }
                          />
                        ))}
                      </ul>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
          <ScheduledDeliveryModal
            handleClick={(k) => {
              setcalculatedOrderForm({ ...calculatedOrderForm, ...k });
              setShowScheduledDeliveryModal(false);
            }}
            isOpen={showScheduledDeliveryModal}
            onClose={() => setShowScheduledDeliveryModal(false)}
          />

          <AddressModal
            open={showAddressModal}
            onClose={() => setShowAddressModal(false)}
            onConfirm={(address) => {
              handleUpdateCalculatedOrderForm(address);
            }}
            defaultSelectedAddress={calculatedOrderForm.address_details}
          />
          <CancelCalculatedOrderModal
            isOpen={showCancelOrderModal}
            onClose={() => setShowCancelOrderModal(false)}
          />
          <DiscountCodeModal
            show={showDiscountCodeModal}
            onClose={() => setShowDiscountCodeModal(false)}
          />
          <AppreciationModal
            show={showAppreciationNoteModal}
            onClose={() => setShowAppreciationNoteModal(false)}
          />
          <AddNewNumberModal
            show={showPhoneNumberModal}
            onClose={() => setShowPhoneNumberModal(false)}
          />
        </div>
      }
    </Fragment>
  );
};

export default BatchBasket;
